/* ============================================================
   Список турниров: единая хронологическая витрина без промежуточных
   фильтров — пользователь сразу видит актуальную карточку.
   ============================================================ */
import { useFedStore, useL } from '../store';
import { useFedContent } from '../content';
import { coverTour, ST_COLOR, ST_BG, hasEntryFee, teamRegistrationOpen, tournamentPhase, sortTournaments, registrationPending, registrationOpensLabel, tl } from '../data';
import DotaPoster from '../components/DotaPoster';
import useTournamentClock from '../useTournamentClock';
import { Ic } from '../icons';
import { Rv, PageHead } from '../ui';

export default function FedTournaments() {
  const { go, lang } = useFedStore();
  const L = useL();
  const tournaments = useFedContent((s) => s.tournaments);
  useTournamentClock(tournaments);

  const stLabel = {
    done: L.st_done,
    live: L.st_live,
    soon: L.st_soon,
    registration: lang === 'kz' ? 'Тіркелу ашық' : 'Регистрация открыта',
    closed: lang === 'kz' ? 'Тіркелу жабық' : 'Регистрация закрыта',
  };
  const list = sortTournaments(tournaments);

  return (
    <div className="fed-pagewrap max-w-[1320px] mx-auto" style={{ padding: '52px 24px 72px' }}>
      <PageHead eyebrow={L.brandSub} title={L.tour_h1} sub={L.tour_sub} />

      <div className="grid gap-[18px] auto-rows-fr" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(min(100%,320px),1fr))' }}>
        {list.map((t, i) => (
          <Rv key={t.id} delay={(i % 3) * 0.07} className="fed-hover-lift flex flex-col overflow-hidden rounded-[18px] bg-surface border border-line" style={{ '--lift': '-5px' }}>
            <div className="relative overflow-hidden flex-shrink-0" style={{ height: t.teamGame === 'dota2' && t.image ? 280 : 200, background: t.teamGame === 'dota2' && t.image ? '#08080b' : coverTour(t.hue) }}>
              {t.teamGame === 'dota2' && !t.image && <DotaPoster tour={t} lang={lang} compact />}
              {(t.image || t.id === 'tdq0g2qy') && (
                <img src={t.id === 'tdq0g2qy' ? '/assets/5298879394677990360.jpg' : t.image} alt={tl(t, 'title', lang)} className="absolute inset-0 w-full h-full" style={t.teamGame === 'dota2' ? { objectFit: 'contain', padding: '44px 0 24px' } : { objectFit: 'cover', objectPosition: t.id === 'tdq0g2qy' ? 'center 35%' : 'center 6%' }} />
              )}
              {t.image && t.id !== 'tdq0g2qy' && t.teamGame !== 'dota2' && <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg,rgba(6,6,8,.15),rgba(6,6,8,.55))' }} />}
              <span className="absolute font-display font-bold text-white rounded-[7px]" style={{ top: 14, left: 14, padding: '5px 11px', fontSize: 12, letterSpacing: '.08em', background: 'rgba(6,6,8,.6)', backdropFilter: 'blur(6px)' }}>
                {t.disc}
              </span>
              {teamRegistrationOpen(t) ? (
                <span
                  className="absolute inline-flex items-center gap-1.5 rounded-full font-mono uppercase"
                  style={{ top: 14, right: 14, padding: '5px 11px', fontSize: 10, letterSpacing: '.1em', background: 'rgba(67,217,106,.16)', color: 'var(--fed-ok)', border: '1px solid rgba(67,217,106,.4)' }}
                >
                  <span className="fed-dot" style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--fed-ok)', boxShadow: '0 0 8px var(--fed-ok)' }} />
                  {lang === 'kz' ? 'Тіркелу ашық' : 'Рег. открыта'}
                </span>
              ) : (
                <span
                  className="absolute inline-flex items-center gap-1.5 rounded-full font-mono uppercase border border-line"
                  style={{ top: 14, right: 14, padding: '5px 11px', fontSize: 10, letterSpacing: '.1em', background: ST_BG[tournamentPhase(t)], color: ST_COLOR[tournamentPhase(t)] }}
                >
                  {stLabel[tournamentPhase(t)]}
                </span>
              )}
              <div className="absolute font-mono uppercase" style={{ bottom: 12, left: 14, fontSize: 10, letterSpacing: '.16em', color: 'rgba(255,255,255,.7)' }}>
                {t.game}
              </div>
            </div>
            <div className="p-5 flex flex-col gap-3.5 flex-1">
              <h3 style={{ fontSize: 23 }}>{tl(t, 'title', lang)}</h3>
              <div className="flex flex-col gap-2 text-fg-muted" style={{ fontSize: 13 }}>
                {/* строку показываем только с заполненным фактом: у турнира без
                    объявленных дат «дата» отсутствует — пустая строка не нужна */}
                {[['calendar', tl(t, 'date', lang)], ['pin', tl(t, 'place', lang)], ['gamepad', t.platform]]
                  .filter(([, v]) => v)
                  .map(([ic, v]) => (
                    <span key={ic} className="flex items-center gap-[9px]"><span className="w-4 inline-flex text-accent-bright"><Ic name={ic} size={16} /></span>{v}</span>
                  ))}
                {t.teamGame === 'dota2' && <span className="flex items-center gap-[9px]"><Ic name="trophy" size={16} />{tl(t, 'prize', lang)}{hasEntryFee(t) && tl(t, 'fee', lang) && ` · ${tl(t, 'fee', lang)}`}</span>}
              </div>
              {registrationPending(t) && <p className="text-fg-soft" style={{ fontSize: 13, lineHeight: 1.5 }}>{registrationOpensLabel(t, lang)}</p>}
              <button
                onClick={() => go('tournament', { tourId: t.id, tourTab: teamRegistrationOpen(t) ? 'reg' : 'overview' })}
                className="fed-btn-ghost mt-auto h-11 rounded-[11px]"
                style={{ fontSize: 14.5 }}
              >
                {(teamRegistrationOpen(t) ? L.tour_reg : L.tour_open)} →
              </button>
            </div>
          </Rv>
        ))}
      </div>
    </div>
  );
}
